import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Customer-side state for the proactive human-handoff offer. Kept in its own slice rather than on
 * the chat message model so the LiveChat feature stays a clean, removable seam (it mirrors the
 * backend, where offer state lives in a LiveChat-owned store, not on the core chat message).
 *
 * The offer is populated either by the live SignalR push (ReceiveEscalationOffer) or by the
 * load-time fetch of GET /livechat/customer/offer; `dismissed` hides the card locally for an
 * instant round-trip-free hide while the dismiss call persists the same state on the server.
 */
export interface EscalationOfferEntry {
    messageId: string;
    dismissed: boolean;
}

export interface EscalationOfferState {
    offers: Record<string, EscalationOfferEntry>;
}

const initialState: EscalationOfferState = {
    offers: {},
};

export const escalationOfferSlice = createSlice({
    name: 'escalationOffer',
    initialState,
    reducers: {
        setEscalationOffer: (state, action: PayloadAction<{ chatId: string; messageId: string }>) => {
            const { chatId, messageId } = action.payload;
            state.offers[chatId] = { messageId, dismissed: false };
        },
        dismissEscalationOffer: (state, action: PayloadAction<{ chatId: string }>) => {
            // Record index access is typed non-undefined here, but a dismiss for a chat with no
            // offer is possible at runtime — narrow explicitly before touching the entry.
            const entry = state.offers[action.payload.chatId] as EscalationOfferEntry | undefined;
            if (entry) {
                entry.dismissed = true;
            }
        },
    },
});

export const { setEscalationOffer, dismissEscalationOffer } = escalationOfferSlice.actions;

export default escalationOfferSlice.reducer;
